/**
 * asnOnePagerConstants.ts
 * Column / grain contract for the ASN Gap "Location Summary" ("One Pager")
 * location table — driven by `POST /api/asn/location-details`.
 *
 * Self-contained (no dependency on the retired `asnGapLocationFacts.ts`
 * mock/fixture model): every type here is derived directly from the real
 * `ASNLocationDetailsRow` / `ASNLocationDetailsGrandTotal` API contract in
 * `src/screens/NavigatorReports/pages/AsnGap/api/asnOverviewKpiApi.ts`.
 */

import {
  ASNLocationDetailsGrandTotal,
  ASNLocationDetailsRow,
  AsnLocationGrainKey,
} from '../api/asnOverviewKpiApi';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Metric ids available on the ASN Gap Location Summary table. `pctNonAsn`
 * is derived client-side from `nonAsnLoads`/`totalLoads` per row (the API
 * does not return it directly on each row); every other metric — including
 * the Grand Total's `pctNonAsn` — is rendered from the backend value as-is. */
export type AsnMetricKey = 'nonAsnLoads' | 'totalLoads' | 'pctNonAsn' | 'pyPct' | 'avgSkus' | 'caseQuantity' | 'memberCount';

/** A table column can be sorted by a grain dimension or by a metric */
export type AsnSortKey = AsnLocationGrainKey | AsnMetricKey;

export type AsnSortDirection = 'asc' | 'desc';

/** Display format for a metric value */
export type AsnMetricFormat = 'int' | 'pct2' | 'num1';

/** Direction that counts as "good" for a metric with a target */
export type AsnMetricDirection = 'hi' | 'lo';

/** ASN equivalent of PagerColumn — no `sparc`, no per-row target payload */
export interface AsnMetricColumn {
  readonly id: AsnMetricKey;
  readonly label: string;
  readonly fmt: AsnMetricFormat;
  /** Optional fixed target; only `% Non-ASN` is measured against one today */
  readonly target?: number;
  /** Which side of the target is favourable */
  readonly dir?: AsnMetricDirection;
}

export interface AsnGrainDimension {
  readonly key: AsnLocationGrainKey;
  readonly label: string;
}

export interface AsnTableSortState {
  readonly col: AsnSortKey;
  readonly dir: AsnSortDirection;
}

/** One aggregated table row, ready for the shared OnePager rendering
 * pipeline: grain values on the sticky side, metrics on the scrollable
 * side. Mirrors the API's `ASNLocationDetailsRow` shape plus the
 * client-derived `pctNonAsn`. */
export interface AsnGroupedRow {
  /** Stable identity — use directly as the row's React/FlatList key. */
  readonly key: string;
  /** Grain value per selected grain key (sticky columns) */
  readonly grainVals: Partial<Record<AsnLocationGrainKey, string>>;
  /** Pre-computed metric values keyed by column id */
  readonly vals: Record<AsnMetricKey, number>;
  /** Number of source facts folded into this row */
  readonly memberCount: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Backend target for % Non-ASN Loads (unchanged from the prior mock model). */
export const TARGET_NONASN = 25.0;

export const ASN_METRIC_COLUMNS: AsnMetricColumn[] = [
  { id: 'nonAsnLoads', label: 'Non-ASN Loads', fmt: 'int' },
  { id: 'totalLoads', label: 'Total Loads', fmt: 'int' },
  { id: 'pctNonAsn', label: '% Non-ASN', fmt: 'pct2', target: TARGET_NONASN, dir: 'lo' },
  { id: 'avgSkus', label: 'Avg SKUs', fmt: 'num1' },
  { id: 'caseQuantity', label: 'Case Qty', fmt: 'int' },
  { id: 'memberCount', label: 'Member Count', fmt: 'int' },
];

export const ASN_METRIC_KEYS: AsnMetricKey[] = ASN_METRIC_COLUMNS.map((col) => col.id);

/** Every grain the Chart Configuration panel can group the table by, in
 * dropdown order. */
export const ASN_GRAIN_DIMENSIONS: AsnGrainDimension[] = [
  { key: 'region', label: 'Region / Unit' },
  { key: 'market', label: 'Market / POD' },
  { key: 'location', label: 'Location' },
  { key: 'year', label: 'Year' },
  { key: 'period', label: 'Period' },
  { key: 'week', label: 'Week' },
];

/** Maximum number of grain (sticky) columns the config panel allows */
export const MAX_GRAIN_COLUMNS = 6;

/** Grain selection the table falls back to — matches the sample
 * `/api/asn/location-details` request payload's default view. */
export const DEFAULT_GRAIN_SELECTIONS: AsnLocationGrainKey[] = [
  'region',
  'market',
  'location',
  'year',
  'period',
  'week',
];

export const DEFAULT_ASN_SORT: AsnTableSortState = { col: 'nonAsnLoads', dir: 'desc' };

export const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** Default page size — matches the sample request payload. */
export const DEFAULT_ASN_PAGE_SIZE = 25;

/** Rows rendered before the table body becomes internally scrollable */
export const MAX_VISIBLE_ROWS = 10;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const METRIC_KEY_SET = new Set<string>(ASN_METRIC_KEYS);

export const isAsnMetricKey = (key: string): key is AsnMetricKey => METRIC_KEY_SET.has(key);

export const getAsnGrainLabel = (key: AsnLocationGrainKey): string => {
  const dimension = ASN_GRAIN_DIMENSIONS.find((dim) => dim.key === key);
  return dimension ? dimension.label : key;
};

export const formatAsnMetricValue = (value: number | undefined, col: AsnMetricColumn): string => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  switch (col.fmt) {
    case 'pct2':
      return `${value.toFixed(2)}%`;
    case 'num1':
      return value.toFixed(1);
    case 'int':
    default:
      return Math.round(value).toLocaleString('en-US');
  }
};

/** True when the value sits on the unfavourable side of the column target */
export const isAsnMetricOverTarget = (col: AsnMetricColumn, value: number | undefined): boolean => {
  if (value === undefined || col.target === undefined) return false;
  return col.dir === 'hi' ? value < col.target : value > col.target;
};

/**
 * Derive `% Non-ASN Loads` for a single row — the API does not return this
 * directly (only the Grand Total carries a backend-computed `pctNonAsn`),
 * so it is calculated client-side from the row's own `nonAsnLoads` /
 * `totalLoads`, per the documented formula. Never applied to the Grand
 * Total, which always uses the backend-provided value untouched.
 */
export const calculateAsnRowPctNonAsn = (nonAsnLoads: number, totalLoads: number): number =>
  totalLoads > 0 ? (nonAsnLoads / totalLoads) * 100 : 0;

/**
 * Convert a raw API row into the table-ready `AsnGroupedRow` shape,
 * materializing every metric (including the client-derived `pctNonAsn`) so
 * rows never recompute during render.
 */
export const buildAsnGroupedRowFromApi = (row: ASNLocationDetailsRow): AsnGroupedRow => {
  const { nonAsnLoads, totalLoads, avgSkus, caseQuantity, pyPct } = row.vals;
  return {
    key: row.key,
    grainVals: row.grainVals,
    memberCount: row.memberCount,
    vals: {
      nonAsnLoads,
      totalLoads,
      pctNonAsn: calculateAsnRowPctNonAsn(nonAsnLoads, totalLoads),
      pyPct: pyPct ?? 0,
      avgSkus,
      caseQuantity,
      memberCount: row.memberCount,
    },
  };
};

/**
 * Convert the API's Grand Total into the same metric-value shape the table
 * columns read from, using every backend-provided value as-is (including
 * `pctNonAsn` — never recalculated). `memberCount` has no backend-provided
 * grand total equivalent (summing only the current page's member counts
 * would misrepresent the true total across all pages), so it renders blank.
 */
export const buildAsnGrandTotalMetricValues = (
  grandTotal: ASNLocationDetailsGrandTotal,
): Record<AsnMetricKey, number> => ({
  nonAsnLoads: grandTotal.nonAsnLoads,
  totalLoads: grandTotal.totalLoads,
  pctNonAsn: grandTotal.pctNonAsn,
  pyPct: 0,
  avgSkus: grandTotal.avgSkus,
  caseQuantity: grandTotal.caseQuantity,
  memberCount: NaN,
});

/**
 * Extract a metric value from a Rollup object (used by AsnCompanionTable).
 * Adapts the `Rollup` model from `asnGapLocationFacts` to the `AsnMetricKey`
 * naming convention used by the One Pager.
 *
 * Note: The Rollup model has a different shape than AsnGroupedRow:
 * - Rollup.ny → nonAsnLoads
 * - Rollup.loads → totalLoads
 * - Rollup.pct → pctNonAsn (already calculated as percentage)
 * - Rollup.skus / Rollup.loads → avgSkus (calculated)
 * - Rollup.cases → caseQuantity
 */
export const asnMetricValue = (
  rollup: {
    loads: number;
    ny: number;
    cases: number;
    skus: number;
    pct: number;
  },
  key: AsnMetricKey,
): number => {
  switch (key) {
    case 'nonAsnLoads':
      return rollup.ny;
    case 'totalLoads':
      return rollup.loads;
    case 'pctNonAsn':
      return rollup.pct;
    case 'avgSkus':
      return rollup.loads > 0 ? rollup.skus / rollup.loads : 0;
    case 'caseQuantity':
      return rollup.cases;
    case 'memberCount':
      return 0; // memberCount is not available in Rollup model
    default:
      return 0;
  }
};
